import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

/**
 * Filtros de "Otros pagos", con la misma forma que `SepaFilter`: buscador,
 * botón de filtros con el contador de activos y un panel plegable.
 *
 * Los tres filtros son EXCLUYENTES entre sí, no acumulativos como los de SEPA.
 * Son tres preguntas distintas sobre el mismo mes ("no tiene cuenta por cobrar"
 * / "la tiene y está sin pagar") y cruzarlas devuelve conjuntos que nadie pide;
 * marcar uno desmarca los demás, que es como funcionaba antes de rediseñar esto.
 */

export type OtherPaymentsFilterKey =
    | 'monthWithoutCharge'
    | 'monthWithoutPayment'
    | 'anyMonthWithoutPayment';

// Cómo paga (tipoPago de la suscripción). Va aparte de los tres de arriba y SÍ
// se combina con ellos: "efectivo con deudas del mes" es justo lo que se pide
// para saber a quién cobrar en mano.
export type MetodoPagoFiltro = '' | 'Tarjeta' | 'Efectivo';

export interface OtherPaymentsFilterState {
    searchTerm: string;
    metodoPago: MetodoPagoFiltro;
    monthWithoutCharge: boolean;
    monthWithoutPayment: boolean;
    anyMonthWithoutPayment: boolean;
}

interface OtherPaymentsFilterProps {
    onFilterChange: (filters: OtherPaymentsFilterState) => void;
}

const FILTER_LABELS: { key: OtherPaymentsFilterKey; label: string }[] = [
    { key: 'monthWithoutCharge', label: 'Mes en curso sin cuenta por cobrar' },
    { key: 'monthWithoutPayment', label: 'Con deudas del mes en curso' },
    { key: 'anyMonthWithoutPayment', label: 'Con deudas de cualquier mes' },
];

const OtherPaymentsFilter: React.FC<OtherPaymentsFilterProps> = ({ onFilterChange }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeKey, setActiveKey] = useState<OtherPaymentsFilterKey | null>(null);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [metodoPago, setMetodoPago] = useState<MetodoPagoFiltro>('');

    const emit = (term: string, key: OtherPaymentsFilterKey | null, metodo: MetodoPagoFiltro = metodoPago) => {
        onFilterChange({
            searchTerm: term,
            metodoPago: metodo,
            monthWithoutCharge: key === 'monthWithoutCharge',
            monthWithoutPayment: key === 'monthWithoutPayment',
            anyMonthWithoutPayment: key === 'anyMonthWithoutPayment',
        });
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        emit(value, activeKey);
    };

    const handleToggle = (key: OtherPaymentsFilterKey, checked: boolean) => {
        const next = checked ? key : null;
        setActiveKey(next);
        emit(searchTerm, next);
    };

    const handleMetodoPago = (metodo: MetodoPagoFiltro) => {
        setMetodoPago(metodo);
        emit(searchTerm, activeKey, metodo);
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setActiveKey(null);
        setMetodoPago('');
        emit('', null, '');
    };

    const activeCount = (activeKey !== null ? 1 : 0) + (metodoPago ? 1 : 0);
    const hasActiveFilters = activeCount > 0 || searchTerm !== '';

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, email o teléfono..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                            showAdvancedFilters || activeCount > 0
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        <Filter className="h-4 w-4 mr-2" />
                        Filtros
                        {activeCount > 0 && (
                            <span className="ml-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {activeCount}
                            </span>
                        )}
                    </button>

                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            className="flex items-center px-3 py-2 text-gray-500 hover:text-red-500 border border-gray-300 rounded-lg hover:border-red-300 transition-colors"
                            title="Limpiar filtros"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {showAdvancedFilters && (
                <div className="bg-gray-50 p-4 rounded-lg border">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {FILTER_LABELS.map(({ key, label }) => (
                            <label key={key} className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activeKey === key}
                                    onChange={(e) => handleToggle(key, e.target.checked)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">{label}</span>
                            </label>
                        ))}
                    </div>
                    <p className="mt-3 text-xs text-gray-500">
                        Solo se puede aplicar uno a la vez: al marcar uno se desmarca el anterior.
                    </p>
                    <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-700">Forma de pago:</span>
                        {([['', 'Todas'], ['Tarjeta', 'Tarjeta'], ['Efectivo', 'Efectivo']] as [MetodoPagoFiltro, string][]).map(([valor, label]) => (
                            <button
                                key={label}
                                onClick={() => handleMetodoPago(valor)}
                                className={`px-3 py-1 text-sm rounded-full border ${
                                    metodoPago === valor
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                        <span className="text-xs text-gray-500">Se combina con el filtro de arriba.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OtherPaymentsFilter;
